import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import TableSortLabel from '@mui/material/TableSortLabel';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import AddIcon from '@mui/icons-material/Add';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import Autocomplete from '@mui/material/Autocomplete';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import PeopleIcon from '@mui/icons-material/People';
import {Eraser, Download  } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import { fetchAllSolicitudes, fetchExportarSolicitudes, aprobarSolicitud, rechazarSolicitud, eliminarSolicitud, actualizarSolicitud, crearSolicitud } from '../../peticiones_API/PeticionesVacaciones';
import { fetchPersonasData, fetchNegociosData, fetchDepartamentosData } from '../../peticiones_API/PeticionesGestionPersonasPersonas';
import { fetchPersonasColaboradorasData } from '../../peticiones_API/PeticionesPersonasColaboradoras';
import { fetchEmpresasColaboradorasData } from '../../peticiones_API/PeticionesEmpresasColaboradoras';
import { fetchPeriodosVerano } from '../../peticiones_API/PeticionesPeriodosVerano';
import { fetchPeriodosVeranoColab } from '../../peticiones_API/PeticionesPeriodosVeranoColab';
import { fetchFestivosCombinados } from '../../peticiones_API/PeticionesFestivos';
import { ConfirmacionEliminar } from '../elementos/eventos/avisos/VentanaEmergente';
import { UsuarioContext } from '../providers/personaContext';
import './GestorAprobaciones.css';

function GestorAprobaciones() {
  const ctxUsuario = useContext(UsuarioContext);
  const { persona, ambito, ambitoCargado, ambitoError, recargarAmbito } = ctxUsuario;

  /* ── INCLUIR A QUIEN YA NO ESTÁ ───────────────────────────────────────────
   *
   * APAGADO al entrar, y es deliberado: esta pantalla existe para decidir sobre lo que está vivo.
   * Mezclarlos siempre la llenaría de gente que no vuelve. Pero consultar los días de quien se fue
   * hace falta —liquidación, finiquito— y hasta hoy no había forma: el listado los borraba.
   *
   * El conmutador SÓLO SE OFRECE a quien tiene la clave. Y eso es comodidad, no la defensa: el
   * servidor comprueba el permiso otra vez y, si no lo tiene, **devuelve el listado de siempre**
   * en vez de un error —pedir algo a lo que no se tiene derecho no rompe la pantalla, simplemente
   * no se concede—. */
  const puedeVerInactivos = Array.isArray(ctxUsuario?.permisos)
    && ctxUsuario.permisos.includes('vacaciones.bajas.ver');
  const [incluirBajas, setIncluirBajas] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const filtroPersonaFromUrl = useRef(false);
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState(searchParams.get('persona_id') ? '' : 'pendiente');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState('fecha_inicio');
  const [order, setOrder] = useState('desc');
  const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear());

  const aniosDisponibles = [];
  const anioActual = new Date().getFullYear();
  for (let a = 2024; a <= anioActual + 1; a++) {
    aniosDisponibles.push(a);
  }

  const rolLower = persona?.rol?.toLowerCase() || '';
  const esRolAdmin = ['super administrador', 'recursos humanos'].includes(rolLower);

  // Ámbito de vacaciones resuelto en SERVIDOR (sustituye el parse del token). Solo se migran
  // las ramas de DELEGADO; los fallbacks (esRolAdmin, comodín "todas") se conservan → 281 y
  // demás no-delegados quedan idénticos. El nivel DEPARTAMENTO del token desaparece: Fase 2
  // hizo la delegación por EMPRESA, así que un delegado ve todas las personas/departamentos
  // de sus empresas (= el techo que el backend ya concede). Los desplegables reciben {id,nombre}.
  const gestionNegocioIds = useMemo(() => (ambito?.gestionar?.negocios || []).map(n => n.id), [ambito]);
  const gestionColabIds = useMemo(() => (ambito?.gestionar?.colaboradoras || []).map(c => c.id), [ambito]);

  const tieneVacacionesInt = !!(ambito?.gestionar?.todosNegocios || gestionNegocioIds.length > 0);
  const tieneVacacionesColab = !!(ambito?.gestionar?.todasColaboradoras || gestionColabIds.length > 0);
  const esSoloColab = persona?.es_colaborador && !tieneVacacionesInt && !esRolAdmin;
  // Flag del servidor (incluye supervisión y esquiva la colisión de esSupervisor, medida en A1).
  const puedeVerAprobador = !!ambito?.puedeGestionar;
  const mostrarTabs = !esSoloColab && (esRolAdmin || (tieneVacacionesInt && tieneVacacionesColab));
  const necesitaDatosColab = mostrarTabs || tieneVacacionesColab || esSoloColab;
  const tabInicial = (esSoloColab || (!tieneVacacionesInt && tieneVacacionesColab && !esRolAdmin)) ? 1 : 0;
  const [tabAprobaciones, setTabAprobaciones] = useState(tabInicial);
  const tabInicialRef = useRef(false);

  useEffect(() => {
    if (tabInicialRef.current) return;
    if (esSoloColab || (!tieneVacacionesInt && tieneVacacionesColab && !esRolAdmin)) {
      tabInicialRef.current = true;
      setTabAprobaciones(1);
    }
  }, [esSoloColab, tieneVacacionesInt, tieneVacacionesColab, esRolAdmin]);

  const [showRechazarModal, setShowRechazarModal] = useState(false);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [showEditarModal, setShowEditarModal] = useState(false);
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [editData, setEditData] = useState({
    fecha_inicio: '',
    fecha_fin: '',
    dias_solicitados: 0,
    comentarios: ''
  });

  const [showCrearModal, setShowCrearModal] = useState(false);
  const [personas, setPersonas] = useState([]);
  const [personasColab, setPersonasColab] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [empresasColaboradoras, setEmpresasColaboradoras] = useState([]);
  const [todosDepartamentos, setTodosDepartamentos] = useState([]);
  const [departamentosFiltrados, setDepartamentosFiltrados] = useState([]);
  const [filtroEmpresa, setFiltroEmpresa] = useState([]);
  const [filtroDepartamento, setFiltroDepartamento] = useState([]);
  const [filtroPersona, setFiltroPersona] = useState([]);
  const [filtroEmpresaAplicado, setFiltroEmpresaAplicado] = useState([]);
  const [filtroDepartamentoAplicado, setFiltroDepartamentoAplicado] = useState([]);
  const [filtroPersonaAplicado, setFiltroPersonaAplicado] = useState([]);
  const [empresaInputText, setEmpresaInputText] = useState('');
  const [departamentoInputText, setDepartamentoInputText] = useState('');
  const [personaInputText, setPersonaInputText] = useState('');
  const [periodosVerano, setPeriodosVerano] = useState([]);
  const [festivos, setFestivos] = useState([]);
  const [nuevaSolicitud, setNuevaSolicitud] = useState({
    persona_id: null,
    fecha_inicio: '',
    fecha_fin: '',
    comentarios: ''
  });
  const [personaSeleccionada, setPersonaSeleccionada] = useState(null);

  const confirmDialog = ConfirmacionEliminar();

  const empresasFiltradas = useMemo(() => {
    if (tabAprobaciones === 1) {
      // colaborador: comodín/esRolAdmin → todas; ids del ámbito → filtra; si no → todas (fallback).
      if (ambito?.gestionar?.todasColaboradoras || esRolAdmin) return empresasColaboradoras;
      if (gestionColabIds.length > 0) return empresasColaboradoras.filter(e => gestionColabIds.includes(e.Id));
      return empresasColaboradoras;
    }
    if (ambito?.gestionar?.todosNegocios || esRolAdmin) return empresas;
    if (gestionNegocioIds.length > 0) return empresas.filter(e => gestionNegocioIds.includes(e.Id));
    return empresas;
  }, [empresas, empresasColaboradoras, tabAprobaciones, esRolAdmin, ambito, gestionNegocioIds, gestionColabIds]);

  const personasFiltradas = useMemo(() => {
    if (tabAprobaciones === 1) {
      if (ambito?.gestionar?.todasColaboradoras || esRolAdmin) return personasColab;
      if (gestionColabIds.length > 0) return personasColab.filter(p => gestionColabIds.includes(p.empresa_colaboradora_id));
      return personasColab;
    }
    if (ambito?.gestionar?.todosNegocios || esRolAdmin) return personas.filter(p => p.Cargos?.some(c => c.empresa_id && !c.empresa_colaboradores_id));
    if (gestionNegocioIds.length > 0) {
      // Solo EMPRESA (Fase 2): basta con que el cargo esté en una empresa gestionada;
      // ya no se sub-filtra por departamento (ese nivel desapareció de la delegación).
      return personas.filter(p => p.Cargos?.some(c => gestionNegocioIds.includes(c.empresa_id) && !c.empresa_colaboradores_id));
    }
    return personas;
  }, [personas, personasColab, tabAprobaciones, esRolAdmin, ambito, gestionNegocioIds, gestionColabIds]);

  useEffect(() => {
    const cargarDatosAuxiliares = async () => {
      try {
        const esTabColabActivo = tabAprobaciones === 1;
        const promesas = [
          fetchPersonasData({ incluirBajas }),
          fetchNegociosData(),
          fetchDepartamentosData(),
          esTabColabActivo ? fetchPeriodosVeranoColab() : fetchPeriodosVerano(),
          fetchFestivosCombinados(new Date().getFullYear(), null, null, esTabColabActivo)
        ];
        if (necesitaDatosColab) {
          promesas.push(fetchEmpresasColaboradorasData());
          promesas.push(fetchPersonasColaboradorasData());
        }
        const resultados = await Promise.all(promesas);
        const [personasData, empresasData, departamentosData, periodosData, festivosData] = resultados;
        setPersonas(personasData?.personas || personasData || []);
        setEmpresas(empresasData || []);
        setTodosDepartamentos(departamentosData || []);
        setPeriodosVerano(periodosData || []);
        if (necesitaDatosColab && resultados[5]) {
          setEmpresasColaboradoras((resultados[5] || []).filter(e => e.vacaciones));
        }
        if (necesitaDatosColab && resultados[6]) {
          setPersonasColab(resultados[6] || []);
        }
        const todasFechas = [
          ...(festivosData.nacional || []).map(f => f.fecha),
          ...(festivosData.Negocios || []).map(f => f.fecha),//empresa
          ...(festivosData.provincial || []).map(f => f.fecha),
          ...(festivosData.municipal || []).map(f => f.fecha)
        ];
        setFestivos(todasFechas);
      } catch (error) {
        console.error('Error al cargar datos auxiliares:', error);
      }
    };
    cargarDatosAuxiliares();
  /* `incluirBajas` entra en las dependencias: sin el, encender el conmutador cambiaria la
   * lista de solicitudes y NO la del selector, que es justo el hueco que esto arregla. */
  }, [mostrarTabs, necesitaDatosColab, tabAprobaciones, incluirBajas]);

  useEffect(() => {
    let depsFiltrados;
    if (filtroEmpresa.length > 0) {
      depsFiltrados = todosDepartamentos.filter(dep =>
        dep.Negocios?.some(emp => filtroEmpresa.includes(emp.Id))
      );
    } else {
      const idsEmpresasGestionadas = empresasFiltradas.map(e => e.Id);
      if (idsEmpresasGestionadas.length > 0) {
        depsFiltrados = todosDepartamentos.filter(dep =>
          dep.Negocios?.some(emp => idsEmpresasGestionadas.includes(emp.Id))
        );
      } else {
        depsFiltrados = todosDepartamentos;
      }
    }
    // Fase 2 (solo empresa): el desplegable de departamentos ya NO se sub-filtra por
    // departamentos delegados —ese nivel desapareció de la delegación—; muestra los de las
    // empresas gestionadas (calculado arriba). El backend acota igual.
    setDepartamentosFiltrados(depsFiltrados);
  }, [filtroEmpresa, todosDepartamentos, empresasFiltradas]);

  const empresasOpciones = useMemo(() =>
    [...empresasFiltradas].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')),
    [empresasFiltradas]
  );

  const empresaValue = useMemo(() =>
    empresasFiltradas.filter(e => filtroEmpresa.includes(e.Id)),
    [empresasFiltradas, filtroEmpresa]
  );

  const departamentosOpciones = useMemo(() =>
    [...departamentosFiltrados].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')),
    [departamentosFiltrados]
  );

  /* «Ya no está» es `estado = Baja` (1), NO `activo = 0`. Medido: siete de los ocho `activo = 0`
   * eran «En Proceso» —gente que todavía no ha llegado—, y cuatro de los que SÍ se fueron figuran
   * con `activo = 1`. La columna `activo` suena a «está» y contiene otra cosa.
   *
   * Por VALOR y no por forma: `estado` puede llegar como 1 o como '1'. */
  const ESTADO_BAJA = 1;
  const esBaja = (p) => Number(p?.estado) === ESTADO_BAJA;

  const personasOpciones = useMemo(() => {
    /* ── ESTE FILTRO ERA INALCANZABLE, Y PARECIA SER EL QUE DECIDIA ────────────────────
     *
     * `/api/personas` ya no devolvia inactivos —su `where` los excluia en el servidor—, asi que
     * esta linea no quitaba a nadie. El suelo de verdad estaba en OTRO FICHERO, y aflojar este
     * habria dado la sensacion de arreglarlo sin cambiar nada.
     *
     * Y comparaba con `!== false`, que mira la FORMA del dato y no su valor: el dia que el API
     * devolviera el `tinyint` crudo, `0 !== false` es cierto y habrian pasado TODOS. */
    let lista = personasFiltradas.filter(p => incluirBajas || !esBaja(p));
    if (tabAprobaciones === 1) {
      if (filtroEmpresa.length > 0) {
        lista = lista.filter(p => filtroEmpresa.includes(p.empresa_colaboradora_id));
      }
    } else {
      if (filtroEmpresa.length > 0) {
        lista = lista.filter(p => p.Cargos?.some(c => filtroEmpresa.includes(c.empresa_id)));
      }
      if (filtroDepartamento.length > 0) {
        const deptIds = filtroDepartamento.map(d => d.Id);
        lista = lista.filter(p => p.Cargos?.some(c => deptIds.includes(c.departamento_id)));
      }
    }
    return lista.sort((a, b) => `${a.nombre} ${a.apellidos}`.localeCompare(`${b.nombre} ${b.apellidos}`));
  }, [personasFiltradas, filtroEmpresa, filtroDepartamento, tabAprobaciones, incluirBajas]);

  const aplicarFiltros = useCallback(() => {
    setFiltroEmpresaAplicado(filtroEmpresa);
    setFiltroDepartamentoAplicado(filtroDepartamento);
    setFiltroPersonaAplicado(filtroPersona);
  }, [filtroEmpresa, filtroDepartamento, filtroPersona]);

  // Construye los parametros de ambito/filtros. Compartido por la tabla y la
  // exportacion a Excel para que el Excel refleje EXACTAMENTE lo que se ve.
  const construirParamsSolicitudes = useCallback(() => {
    const params = { anio: anioSeleccionado };
    if (filtroEstado) params.estado = filtroEstado;
    if (filtroEmpresaAplicado.length > 0) {
      params.empresa_id = filtroEmpresaAplicado.join(',');
    }
    if (filtroDepartamentoAplicado.length > 0) {
      params.departamento_id = filtroDepartamentoAplicado.map(d => d.Id).join(',');
    }
    if (filtroPersonaAplicado.length > 0) {
      params.persona_id = filtroPersonaAplicado.map(p => p.Id).join(',');
    }

    // Los 4 params de AUTORIZACIÓN (admin_todas, gestion_vacaciones_int/_empresas/_colab) se
    // retiran: INERTES desde 3a (el backend construye la base desde resolverAmbito, verificado
    // por HTTP). Solo se mantiene `colaborador` (presentación: qué eje/pestaña).
    params.colaborador = tabAprobaciones === 1 ? 'true' : 'false';
    // Va con los demás filtros, así que el Excel de esta pantalla sale igual que la tabla —que es
    // para lo que este constructor está compartido—.
    if (incluirBajas) params.incluir_bajas = '1';
    return params;
  }, [filtroEstado, anioSeleccionado, filtroEmpresaAplicado, filtroDepartamentoAplicado, filtroPersonaAplicado, tabAprobaciones, incluirBajas]);

  const cargarSolicitudes = useCallback(async (mostrarLoading = true) => {
    try {
      if (mostrarLoading) setLoading(true);
      const params = construirParamsSolicitudes();
      const data = await fetchAllSolicitudes(params);
      setSolicitudes(data);
      setPage(0);
    } catch (error) {
      console.error('Error al cargar solicitudes:', error);
      toast.error('Error al cargar las solicitudes');
    } finally {
      setLoading(false);
    }
  }, [construirParamsSolicitudes]);

  const [exportando, setExportando] = useState(false);
  const handleExportarExcel = useCallback(async () => {
    try {
      setExportando(true);
      const params = construirParamsSolicitudes();
      const blob = await fetchExportarSolicitudes(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const hoy = new Date();
      const nombre = `vacaciones-aprobaciones-${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}.xlsx`;
      link.href = url;
      link.download = nombre;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      toast.error('Error al exportar el Excel');
    } finally {
      setExportando(false);
    }
  }, [construirParamsSolicitudes]);

  useEffect(() => {
    cargarSolicitudes();
  }, [cargarSolicitudes]);

  useEffect(() => {
    if (filtroPersonaFromUrl.current) return;
    const personaId = searchParams.get('persona_id');
    const personaNombre = searchParams.get('persona_nombre');
    if (personaId && personas.length > 0) {
      const id = parseInt(personaId);
      const personaEncontrada = personas.find(p => p.Id === id);
      if (personaEncontrada) {
        setFiltroPersona([personaEncontrada]);
        setFiltroPersonaAplicado([personaEncontrada]);
      } else if (personaNombre) {
        const personaObj = { Id: id, nombre: decodeURIComponent(personaNombre).split(' ')[0], apellidos: decodeURIComponent(personaNombre).split(' ').slice(1).join(' ') };
        setFiltroPersona([personaObj]);
        setFiltroPersonaAplicado([personaObj]);
      }
      filtroPersonaFromUrl.current = true;
      searchParams.delete('persona_id');
      searchParams.delete('persona_nombre');
      setSearchParams(searchParams, { replace: true });
    }
  }, [personas, searchParams, setSearchParams]);

  const handleAprobar = async (id) => {
    try {
      await aprobarSolicitud(id);
      toast.success('Solicitud aprobada correctamente');
      cargarSolicitudes(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al aprobar la solicitud');
    }
  };

  const handleRechazar = async () => {
    if (!motivoRechazo.trim()) {
      toast.warning('Debes indicar un motivo de rechazo');
      return;
    }
    try {
      await rechazarSolicitud(solicitudSeleccionada.Id, motivoRechazo);
      toast.success('Solicitud rechazada');
      setShowRechazarModal(false);
      setMotivoRechazo('');
      setSolicitudSeleccionada(null);
      cargarSolicitudes(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al rechazar la solicitud');
    }
  };

  const handleEliminar = async (solicitud) => {
    const mensaje = solicitud.estado === 'aprobada'
      ? 'solicitud de vacaciones aprobada (se restauraran los días al empleado)'
      : 'solicitud de vacaciones';
    const confirmado = await confirmDialog.showAlert('eliminar', mensaje);
    if (confirmado) {
      try {
        await eliminarSolicitud(solicitud.Id);
        toast.success('Solicitud eliminada correctamente');
        cargarSolicitudes(false);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Error al eliminar la solicitud');
      }
    }
  };

  const handleOpenEditar = (solicitud) => {
    setSolicitudSeleccionada(solicitud);
    setEditData({
      fecha_inicio: solicitud.fecha_inicio?.split('T')[0] || '',
      fecha_fin: solicitud.fecha_fin?.split('T')[0] || '',
      dias_solicitados: solicitud.dias_solicitados || 0,
      comentarios: solicitud.comentarios || ''
    });
    setShowEditarModal(true);
  };

  const calcularDias = (fechaInicio, fechaFin) => {
    if (!fechaInicio || !fechaFin) return 0;
    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);
    let dias = 0;
    const actual = new Date(inicio);
    while (actual <= fin) {
      const esFinDeSemana = actual.getDay() === 0 || actual.getDay() === 6;
      const fechaStr = actual.toISOString().split('T')[0];
      const esFestivo = festivos.includes(fechaStr);
      if (!esFinDeSemana && !esFestivo) {
        dias++;
      }
      actual.setDate(actual.getDate() + 1);
    }
    return dias;
  };

  const esPeriodoVerano = (fechaInicio, fechaFin) => {
    if (!fechaInicio || !fechaFin || periodosVerano.length === 0) return false;
    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);
    return periodosVerano.some(periodo => {
      const periodoInicio = new Date(periodo.fecha_inicio);
      const periodoFin = new Date(periodo.fecha_fin);
      return inicio <= periodoFin && fin >= periodoInicio;
    });
  };

  const calcularDiasDesglose = (fechaInicio, fechaFin) => {
    if (!fechaInicio || !fechaFin) return { normales: 0, verano: 0, total: 0 };
    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);
    let normales = 0;
    let verano = 0;
    const actual = new Date(inicio);
    while (actual <= fin) {
      const esFinDeSemana = actual.getDay() === 0 || actual.getDay() === 6;
      const fechaStr = actual.toISOString().split('T')[0];
      const esFestivo = festivos.includes(fechaStr);
      if (!esFinDeSemana && !esFestivo) {
        const enVerano = periodosVerano.some(p => {
          const pInicio = p.fecha_inicio?.split('T')[0];
          const pFin = p.fecha_fin?.split('T')[0];
          return pInicio && pFin && fechaStr >= pInicio && fechaStr <= pFin;
        });
        if (enVerano) verano++;
        else normales++;
      }
      actual.setDate(actual.getDate() + 1);
    }
    return { normales, verano, total: normales + verano };
  };

  const handleCrearSolicitud = async () => {
    if (!personaSeleccionada) {
      toast.warning('Debes seleccionar un trabajador');
      return;
    }
    if (!nuevaSolicitud.fecha_inicio || !nuevaSolicitud.fecha_fin) {
      toast.warning('Las fechas son obligatorias');
      return;
    }
    const diasCalculados = calcularDias(nuevaSolicitud.fecha_inicio, nuevaSolicitud.fecha_fin);
    if (diasCalculados <= 0) {
      toast.error('Los días seleccionados son festivos o fines de semana y no cuentan como días laborables');
      return;
    }
    const esColab = tabAprobaciones === 1;
    try {
      await crearSolicitud({
        ...(esColab
          ? { persona_colaboradora_id: personaSeleccionada.Id }
          : { persona_id: personaSeleccionada.Id }),
        fecha_inicio: nuevaSolicitud.fecha_inicio,
        fecha_fin: nuevaSolicitud.fecha_fin,
        es_periodo_verano: esPeriodoVerano(nuevaSolicitud.fecha_inicio, nuevaSolicitud.fecha_fin),
        dias_solicitados: diasCalculados,
        comentarios: nuevaSolicitud.comentarios
      });
      toast.success('Vacaciones añadidas correctamente');
      setShowCrearModal(false);
      setNuevaSolicitud({ persona_id: null, fecha_inicio: '', fecha_fin: '', comentarios: '' });
      setPersonaSeleccionada(null);
      cargarSolicitudes(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al crear la solicitud');
    }
  };

  const handleGuardarEdicion = async () => {
    if (!editData.fecha_inicio || !editData.fecha_fin) {
      toast.warning('Las fechas son obligatorias');
      return;
    }
    const diasCalculados = calcularDias(editData.fecha_inicio, editData.fecha_fin);
    if (diasCalculados <= 0) {
      toast.warning('Los días seleccionados son festivos o fines de semana y no cuentan como días laborables');
      return;
    }
    try {
      await actualizarSolicitud(solicitudSeleccionada.Id, {
        ...editData,
        dias_solicitados: diasCalculados,
        es_periodo_verano: esPeriodoVerano(editData.fecha_inicio, editData.fecha_fin)
      });
      toast.success('Solicitud actualizada correctamente');
      setShowEditarModal(false);
      setSolicitudSeleccionada(null);
      cargarSolicitudes(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al actualizar la solicitud');
    }
  };

  const handleTabAprobacionesChange = (event, newValue) => {
    setTabAprobaciones(newValue);
    setFiltroEmpresa([]);
    setFiltroDepartamento([]);
    setFiltroPersona([]);
    setFiltroEmpresaAplicado([]);
    setFiltroDepartamentoAplicado([]);
    setFiltroPersonaAplicado([]);
  };

  const handleSort = (columna) => {
    const isAsc = orderBy === columna && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columna);
    setPage(0);
  };

  const solicitudesOrdenadas = [...solicitudes].sort((a, b) => {
    let valorA, valorB;
    switch (orderBy) {
      case 'empleado':
        valorA = `${a.solicitante?.nombre || ''} ${a.solicitante?.apellidos || ''}`.toLowerCase();
        valorB = `${b.solicitante?.nombre || ''} ${b.solicitante?.apellidos || ''}`.toLowerCase();
        break;
      case 'fecha_inicio':
        valorA = a.fecha_inicio || '';
        valorB = b.fecha_inicio || '';
        break;
      case 'fecha_fin':
        valorA = a.fecha_fin || '';
        valorB = b.fecha_fin || '';
        break;
      case 'dias':
        valorA = a.dias_solicitados || 0;
        valorB = b.dias_solicitados || 0;
        break;
      case 'tipo':
        valorA = a.es_periodo_verano ? 1 : 0;
        valorB = b.es_periodo_verano ? 1 : 0;
        break;
      case 'estado':
        valorA = a.estado || '';
        valorB = b.estado || '';
        break;
      case 'comentarios':
        valorA = (a.comentarios || '').toLowerCase();
        valorB = (b.comentarios || '').toLowerCase();
        break;
      default:
        valorA = a.fecha_inicio || '';
        valorB = b.fecha_inicio || '';
    }
    if (order === 'asc') {
      return valorA < valorB ? -1 : valorA > valorB ? 1 : 0;
    } else {
      return valorA > valorB ? -1 : valorA < valorB ? 1 : 0;
    }
  });

  const getEstadoChip = (estado) => {
    const colores = {
      pendiente: 'warning',
      aprobada: 'success',
      rechazada: 'error',
      cancelada: 'default'
    };
    return <Chip label={estado} color={colores[estado] || 'default'} size="small" />;
  };

  const hayComentarios = solicitudes.some(s => s.comentarios && s.comentarios.trim() !== '');
  const mostrarComentarios = filtroEstado === 'pendiente' && hayComentarios;
  const mostrarMotivoRechazo = filtroEstado === 'rechazada';

  return (
    <div className="gestor-aprobaciones">
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: '#336385' }}>
          {!mostrarTabs && tieneVacacionesColab && !tieneVacacionesInt ? 'Aprobación de Solicitudes Colaboradores' : 'Aprobación de Solicitudes'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Download size={18} />}
            onClick={handleExportarExcel}
            disabled={exportando}
            sx={{ borderColor: '#336385', color: '#336385', '&:hover': { borderColor: '#27496d', backgroundColor: 'rgba(51,99,133,0.04)' } }}
          >
            {exportando ? 'Exportando...' : 'Exportar Excel'}
          </Button>
          {puedeVerAprobador && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCrearModal(true)}
              sx={{ backgroundColor: '#336385', '&:hover': { backgroundColor: '#27496d' } }}
            >
              Añadir Vacaciones
            </Button>
          )}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={anioSeleccionado}
              onChange={(e) => setAnioSeleccionado(e.target.value)}
              startAdornment={<CalendarMonthIcon sx={{ mr: 1, color: '#64748b' }} />}
              sx={{ borderRadius: 2 }}
            >
              {aniosDisponibles.map(anio => (
                <MenuItem key={anio} value={anio}>{anio}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Estado</InputLabel>
            <Select
              value={filtroEstado}
              label="Estado"
              onChange={(e) => setFiltroEstado(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="pendiente">Pendientes</MenuItem>
              <MenuItem value="aprobada">Aprobadas</MenuItem>
              <MenuItem value="rechazada">Rechazadas</MenuItem>
            </Select>
          </FormControl>
          {/* SÓLO a quien tiene la clave. Y apagado al entrar: ver abajo el porqué. */}
          {puedeVerInactivos && (
            <FormControlLabel
              sx={{ ml: 0.5 }}
              control={(
                <Switch
                  size="small"
                  checked={incluirBajas}
                  onChange={(e) => setIncluirBajas(e.target.checked)}
                />
              )}
              label={(
                <Tooltip title="Sus vacaciones se pueden consultar, pero no aprobar">
                  <Typography variant="body2" sx={{ color: '#555' }}>
                    Incluir a quien ya no está
                  </Typography>
                </Tooltip>
              )}
            />
          )}
        </Box>
      </Box>

      {mostrarTabs && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={tabAprobaciones}
            onChange={handleTabAprobacionesChange}
            sx={{
              '& .MuiTab-root': {
                fontWeight: 'bold',
                textTransform: 'none',
                fontSize: '1rem'
              },
              '& .Mui-selected': {
                color: '#336385'
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#336385'
              }
            }}
          >
            <Tab icon={<PeopleIcon />} iconPosition="start" label="Emplead@s" />
            <Tab icon={<PeopleIcon />} iconPosition="start" label="Colaboradores" />
          </Tabs>
        </Box>
      )}

      {puedeVerAprobador && (
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Autocomplete
            multiple
            size="small"
            sx={{ minWidth: 200 }}
            options={empresasOpciones}
            inputValue={empresaInputText}
            onInputChange={(e, val, reason) => { if (reason !== 'reset') setEmpresaInputText(val || ''); else setEmpresaInputText(''); }}
            getOptionLabel={(option) => option.nombre || ''}
            value={empresaValue}
            onChange={(e, newValue) => setFiltroEmpresa(newValue.map(v => v.Id))}
            onClose={() => setTimeout(aplicarFiltros, 0)}
            disableCloseOnSelect
            renderOption={({ key, ...props }, option, { selected }) => (
              <li key={key} {...props}>
                <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                {option.nombre}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label= {tabAprobaciones === 1 ? "Empresa" : "Negocio" } />
            )}
            isOptionEqualToValue={(option, value) => option.Id === value?.Id}
          />
          {tabAprobaciones !== 1 && (
          <Autocomplete
            multiple
            size="small"
            sx={{ minWidth: 200 }}
            options={departamentosOpciones}
            inputValue={departamentoInputText}
            onInputChange={(e, val, reason) => { if (reason !== 'reset') setDepartamentoInputText(val || ''); else setDepartamentoInputText(''); }}
            getOptionLabel={(option) => option.nombre || ''}
            value={filtroDepartamento}
            onChange={(e, newValue) => setFiltroDepartamento(newValue)}
            onClose={() => setTimeout(aplicarFiltros, 0)}
            disableCloseOnSelect
            renderOption={({ key, ...props }, option, { selected }) => (
              <li key={key} {...props}>
                <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                {option.nombre}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Departamento" />
            )}
            isOptionEqualToValue={(option, value) => option.Id === value?.Id}
          />
          )}
          <Autocomplete
            multiple
            size="small"
            sx={{ minWidth: 250 }}
            options={personasOpciones}
            inputValue={personaInputText}
            onInputChange={(e, val, reason) => { if (reason !== 'reset') setPersonaInputText(val || ''); else setPersonaInputText(''); }}
            getOptionLabel={(option) => `${option.nombre} ${option.apellidos}`}
            value={filtroPersona}
            onChange={(e, newValue) => setFiltroPersona(newValue)}
            onClose={() => setTimeout(aplicarFiltros, 0)}
            disableCloseOnSelect
            renderOption={({ key, ...props }, option, { selected }) => (
              <li key={key} {...props}>
                <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                {option.nombre} {option.apellidos}
                {/* La misma marca que lleva la fila: quien busca a alguien tiene que saber que ya
                    no esta ANTES de elegirlo, no despues de ver la lista vacia de aprobar. */}
                {esBaja(option) && (
                  <Chip label="Ya no está" size="small" sx={{ ml: 1, height: 18, fontSize: '0.7rem' }} />
                )}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Persona" />
            )}
            isOptionEqualToValue={(option, value) => option.Id === value?.Id}
          />
          <IconButton
            size="small"
            disabled={filtroEmpresa.length === 0 && filtroDepartamento.length === 0 && filtroPersona.length === 0}
            onClick={() => {
              setFiltroEmpresa([]);
              setFiltroDepartamento([]);
              setFiltroPersona([]);
              setFiltroEmpresaAplicado([]);
              setFiltroDepartamentoAplicado([]);
              setFiltroPersonaAplicado([]);
              setEmpresaInputText('');
              setDepartamentoInputText('');
              setPersonaInputText('');
            }}
            sx={{ color: '#336385' }}
          >
            <Eraser size={30} />
          </IconButton>
        </Box>
      )}

      <Card>
        <CardContent>
          {/* Receta de 3 estados del ámbito ANTES de la lista: en error el aviso OCUPA el sitio
              de la lista, NO la acompaña — una bandeja vacía ("nada pendiente") y una no-resuelta
              ("no lo sé") no pueden parecerse en pantalla. */}
          {!ambitoCargado ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6, flexDirection: 'column', gap: 2 }}>
              <CircularProgress size={36} sx={{ color: '#336385' }} />
              <Typography color="text.secondary">Cargando tus datos de vacaciones...</Typography>
            </Box>
          ) : ambitoError ? (
            <Alert severity="warning" action={<Button color="inherit" size="small" onClick={recargarAmbito}>Reintentar</Button>} sx={{ my: 2 }}>
              No se han podido cargar tus datos de vacaciones. No se pueden mostrar las solicitudes ahora mismo; vuelve a intentarlo.
            </Alert>
          ) : loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6, flexDirection: 'column', gap: 2 }}>
              <CircularProgress size={36} sx={{ color: '#336385' }} />
              <Typography color="text.secondary">Cargando solicitudes...</Typography>
            </Box>
          ) : solicitudes.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No hay solicitudes {filtroEstado ? `con estado "${filtroEstado}"` : ''}
            </Typography>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>
                      <TableSortLabel active={orderBy === 'empleado'} direction={orderBy === 'empleado' ? order : 'asc'} onClick={() => handleSort('empleado')}>
                        <strong>Empleado</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={orderBy === 'fecha_inicio'} direction={orderBy === 'fecha_inicio' ? order : 'asc'} onClick={() => handleSort('fecha_inicio')}>
                        <strong>Fecha Inicio</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={orderBy === 'fecha_fin'} direction={orderBy === 'fecha_fin' ? order : 'asc'} onClick={() => handleSort('fecha_fin')}>
                        <strong>Fecha Fin</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={orderBy === 'dias'} direction={orderBy === 'dias' ? order : 'asc'} onClick={() => handleSort('dias')}>
                        <strong>Días</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={orderBy === 'tipo'} direction={orderBy === 'tipo' ? order : 'asc'} onClick={() => handleSort('tipo')}>
                        <strong>Tipo</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell><strong>Estado</strong></TableCell>
                    {mostrarComentarios ? (
                      <TableCell>
                        <TableSortLabel active={orderBy === 'comentarios'} direction={orderBy === 'comentarios' ? order : 'asc'} onClick={() => handleSort('comentarios')}>
                          <strong>Comentarios</strong>
                        </TableSortLabel>
                      </TableCell>
                    ) : mostrarMotivoRechazo ? (
                      <TableCell><strong>Rechazado por</strong></TableCell>
                    ) : puedeVerAprobador ? (
                      <TableCell><strong>Aprobado por</strong></TableCell>
                    ) : null}
                    <TableCell align="center"><strong>Acciones</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {solicitudesOrdenadas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((solicitud) => (
                    <TableRow key={solicitud.Id} hover>
                      <TableCell>
                        {solicitud.solicitante?.nombre} {solicitud.solicitante?.apellidos}
                        {/* Que se NOTE cuál ya no está. Una lista donde unos están y otros no, sin
                            distinguirlos, es peor que una que no los mezcla: quien la lea tiene que
                            poder ver de un vistazo a quién no tiene sentido escribirle. */}
                        {esBaja(solicitud.solicitante) && (
                          <Chip label="Ya no está" size="small"
                            sx={{ ml: 1, height: 18, fontSize: '0.65rem', bgcolor: '#e0e0e0', color: '#555' }} />
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(solicitud.fecha_inicio).toLocaleDateString('es-ES')}
                      </TableCell>
                      <TableCell>
                        {new Date(solicitud.fecha_fin).toLocaleDateString('es-ES')}
                      </TableCell>
                      <TableCell>{solicitud.dias_solicitados}</TableCell>
                      <TableCell>
                        {solicitud.dias_verano > 0 && solicitud.dias_verano < solicitud.dias_solicitados ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip label={`${solicitud.dias_solicitados - solicitud.dias_verano} Normal`} size="small" variant="outlined" />
                            <Chip label={`${solicitud.dias_verano} Verano`} size="small" sx={{ backgroundColor: '#ff9800', color: 'white' }} />
                          </Box>
                        ) : solicitud.es_periodo_verano ? (
                          <Chip label="Verano" size="small" sx={{ backgroundColor: '#ff9800', color: 'white' }} />
                        ) : (
                          <Chip label="Normal" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>{getEstadoChip(solicitud.estado)}</TableCell>
                      {mostrarComentarios ? (
                        <TableCell>
                          {solicitud.comentarios ? (
                            <Typography variant="body2" sx={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {solicitud.comentarios}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                      ) : mostrarMotivoRechazo ? (
                        <TableCell>
                          {solicitud.motivo_rechazo ? (
                            <Tooltip title={solicitud.motivo_rechazo} arrow>
                              <Typography variant="body2" sx={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {solicitud.motivo_rechazo}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                      ) : puedeVerAprobador ? (
                        <TableCell>
                          {solicitud.aprobador ? (
                            `${solicitud.aprobador.nombre} ${solicitud.aprobador.apellidos}`
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                      ) : null}
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <Tooltip title="Ver detalle">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSolicitudSeleccionada(solicitud);
                                setShowDetalleModal(true);
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {solicitud.estado === 'pendiente' && (
                            <>
                              {/* Con el filtro encendido pueden salir solicitudes de quien ya no
                                  está. Aprobarlas no significa nada —y moveria su contador de días,
                                  que es justo el dato por el que se abre esta pantalla—. Si una
                                  sigue pendiente, es una SEÑAL de que quedó sin resolver al darle
                                  de baja: el camino es rechazarla, no aprobarla.
                                  El servidor lo rechaza igual con un 409: esto es comodidad. */}
                              <Tooltip title={esBaja(solicitud.solicitante)
                                ? 'No se aprueba a quien ya no está: recházala o bórrala'
                                : 'Aprobar'}>
                                <span>
                                <IconButton
                                  size="small"
                                  color="success"
                                  disabled={esBaja(solicitud.solicitante)}
                                  onClick={() => handleAprobar(solicitud.Id)}
                                >
                                  <CheckCircleIcon fontSize="small" />
                                </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Rechazar">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setSolicitudSeleccionada(solicitud);
                                    setShowRechazarModal(true);
                                  }}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {puedeVerAprobador && (solicitud.estado === 'pendiente' || solicitud.estado === 'aprobada') && (() => {
                            const hoy = new Date();
                            hoy.setHours(0, 0, 0, 0);
                            const fechaInicio = new Date(solicitud.fecha_inicio);
                            fechaInicio.setHours(0, 0, 0, 0);
                            return fechaInicio >= hoy;
                          })() && (
                            <>
                              <Tooltip title="Editar">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenEditar(solicitud)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                              <Tooltip title="Eliminar">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleEliminar(solicitud)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {solicitudes.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: '#336385', fontWeight: 'medium' }}>
              REGISTROS A MOSTRAR
            </Typography>
            <Select
              size="small"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              sx={{ minWidth: 70 }}
            >
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={75}>75</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={200}>200</MenuItem>
            </Select>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              sx={{ color: '#336385' }}
            >
              {'<'}
            </IconButton>
            {(() => {
              const totalPages = Math.ceil(solicitudes.length / rowsPerPage);
              const pages = [];
              if (totalPages <= 5) {
                for (let i = 0; i < totalPages; i++) {
                  pages.push(i);
                }
              } else {
                pages.push(0);
                if (page > 2) pages.push('...');
                for (let i = Math.max(1, page - 1); i <= Math.min(totalPages - 2, page + 1); i++) {
                  if (!pages.includes(i)) pages.push(i);
                }
                if (page < totalPages - 3) pages.push('...');
                pages.push(totalPages - 1);
              }
              return pages.map((p, idx) => (
                p === '...' ? (
                  <Typography key={`ellipsis-${idx}`} variant="body2" sx={{ px: 1 }}>...</Typography>
                ) : (
                  <Button
                    key={p}
                    size="small"
                    variant={page === p ? 'contained' : 'text'}
                    onClick={() => setPage(p)}
                    sx={{
                      minWidth: 32,
                      height: 32,
                      p: 0,
                      backgroundColor: page === p ? '#336385' : 'transparent',
                      color: page === p ? 'white' : '#336385',
                      '&:hover': { backgroundColor: page === p ? '#27496d' : '#e3f2fd' }
                    }}
                  >
                    {p + 1}
                  </Button>
                )
              ));
            })()}
            <IconButton
              size="small"
              onClick={() => setPage(Math.min(Math.ceil(solicitudes.length / rowsPerPage) - 1, page + 1))}
              disabled={page >= Math.ceil(solicitudes.length / rowsPerPage) - 1}
              sx={{ color: '#336385' }}
            >
              {'>'}
            </IconButton>
          </Box>

          <Typography variant="body2" sx={{ color: '#336385', fontWeight: 'bold' }}>
            Total registros: {solicitudes.length}
          </Typography>
        </Box>
      )}

      <Dialog open={showRechazarModal} onClose={() => setShowRechazarModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#d32f2f', color: 'white' }}>
          Rechazar Solicitud
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Motivo del rechazo"
            multiline
            rows={3}
            sx={{ mt: 3 }}
            value={motivoRechazo}
            onChange={(e) => setMotivoRechazo(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRechazarModal(false)} sx={{ color: '#333' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRechazar}
          >
            Rechazar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showDetalleModal} onClose={() => setShowDetalleModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#336385', color: 'white' }}>
          Detalle de Solicitud
        </DialogTitle>
        <DialogContent>
          {solicitudSeleccionada && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ mb: 1 }}>
                <strong>Empleado:</strong> {solicitudSeleccionada.Persona?.nombre} {solicitudSeleccionada.Persona?.apellidos}
              </Typography>
              <Typography sx={{ mb: 1 }}>
                <strong>Fecha Inicio:</strong> {new Date(solicitudSeleccionada.fecha_inicio).toLocaleDateString('es-ES')}
              </Typography>
              <Typography sx={{ mb: 1 }}>
                <strong>Fecha Fin:</strong> {new Date(solicitudSeleccionada.fecha_fin).toLocaleDateString('es-ES')}
              </Typography>
              <Typography sx={{ mb: 1 }}>
                <strong>Días Solicitados:</strong> {solicitudSeleccionada.dias_solicitados}
              </Typography>
              <Typography sx={{ mb: 1 }}>
                <strong>Tipo:</strong> {solicitudSeleccionada.dias_verano > 0 && solicitudSeleccionada.dias_verano < solicitudSeleccionada.dias_solicitados
                  ? `${solicitudSeleccionada.dias_solicitados - solicitudSeleccionada.dias_verano} Normal + ${solicitudSeleccionada.dias_verano} Verano`
                  : solicitudSeleccionada.es_periodo_verano ? 'Periodo de Verano' : 'Normal'}
              </Typography>
              <Typography sx={{ mb: 1 }}>
                <strong>Estado:</strong> {getEstadoChip(solicitudSeleccionada.estado)}
              </Typography>
              {solicitudSeleccionada.comentarios && (
                <Typography sx={{ mb: 1 }}>
                  <strong>Comentarios:</strong> {solicitudSeleccionada.comentarios}
                </Typography>
              )}
              {solicitudSeleccionada.motivo_rechazo && (
                <Typography sx={{ mb: 1, color: 'error.main' }}>
                  <strong>Motivo Rechazo:</strong> {solicitudSeleccionada.motivo_rechazo}
                </Typography>
              )}
              {puedeVerAprobador && solicitudSeleccionada.aprobador && (
                <Typography sx={{ mb: 1 }}>
                  <strong>Aprobado por:</strong> {solicitudSeleccionada.aprobador.nombre} {solicitudSeleccionada.aprobador.apellidos}
                </Typography>
              )}
              <Typography sx={{ mb: 1 }}>
                <strong>Fecha Solicitud:</strong> {new Date(solicitudSeleccionada.createdAt).toLocaleString('es-ES')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetalleModal(false)} sx={{ color: '#336385' }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showEditarModal} onClose={() => setShowEditarModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#336385', color: 'white' }}>
          Editar Solicitud de Vacaciones
        </DialogTitle>
        <DialogContent>
          {solicitudSeleccionada && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                <strong>Empleado:</strong> {solicitudSeleccionada.solicitante?.nombre} {solicitudSeleccionada.solicitante?.apellidos}
              </Typography>
              <TextField
                fullWidth
                label="Fecha Inicio"
                type="date"
                value={editData.fecha_inicio}
                onChange={(e) => {
                  const nuevaFechaInicio = e.target.value;
                  if (editData.fecha_fin && editData.fecha_fin < nuevaFechaInicio) {
                    setEditData({ ...editData, fecha_inicio: nuevaFechaInicio, fecha_fin: '' });
                  } else {
                    setEditData({ ...editData, fecha_inicio: nuevaFechaInicio });
                  }
                }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Fecha Fin"
                type="date"
                value={editData.fecha_fin}
                onChange={(e) => setEditData({ ...editData, fecha_fin: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: editData.fecha_inicio || undefined }}
              />
              {editData.fecha_inicio && editData.fecha_fin && (() => {
                const desglose = calcularDiasDesglose(editData.fecha_inicio, editData.fecha_fin);
                return (
                  <Box sx={{ p: 2, backgroundColor: desglose.verano > 0 ? '#ffedd5' : '#e3f2fd', borderRadius: 1 }}>
                    <Typography variant="body1">
                      <strong>Días laborables:</strong> {desglose.total}
                    </Typography>
                    {desglose.verano > 0 && desglose.normales > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 2 }}>
                        <Typography variant="body2" sx={{ color: '#0284c7', fontWeight: 500 }}>
                          {desglose.normales} día{desglose.normales !== 1 ? 's' : ''} normal{desglose.normales !== 1 ? 'es' : ''}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <BeachAccessIcon sx={{ color: '#ea580c', mr: 0.5, fontSize: 18 }} />
                          <Typography variant="body2" sx={{ color: '#ea580c', fontWeight: 500 }}>
                            {desglose.verano} día{desglose.verano !== 1 ? 's' : ''} de verano
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    {desglose.verano > 0 && desglose.normales === 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <BeachAccessIcon sx={{ color: '#ea580c', mr: 1 }} />
                        <Typography variant="body2" sx={{ color: '#ea580c', fontWeight: 500 }}>
                          Periodo de verano
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })()}
              <TextField
                fullWidth
                label="Comentario"
                multiline
                rows={2}
                value={editData.comentarios}
                onChange={(e) => setEditData({ ...editData, comentarios: e.target.value })}
              />
              {solicitudSeleccionada.estado === 'aprobada' && (
                <Alert severity="warning">
                  Al modificar una solicitud aprobada, se ajustarán automáticamente los días de vacaciones del empleado.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditarModal(false)} sx={{ color: '#333' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: '#336385' }}
            onClick={handleGuardarEdicion}
          >
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showCrearModal} onClose={() => setShowCrearModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#336385', color: 'white' }}>
          Añadir Vacaciones a Trabajador
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={personasOpciones}
              getOptionLabel={(option) => `${option.nombre} ${option.apellidos}`}
              value={personaSeleccionada}
              onChange={(e, newValue) => setPersonaSeleccionada(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Seleccionar trabajador" required />
              )}
              isOptionEqualToValue={(option, value) => option.Id === value?.Id}
            />
            <TextField
              fullWidth
              label="Fecha Inicio"
              type="date"
              value={nuevaSolicitud.fecha_inicio}
              onChange={(e) => {
                const nuevaFechaInicio = e.target.value;
                if (nuevaSolicitud.fecha_fin && nuevaSolicitud.fecha_fin < nuevaFechaInicio) {
                  setNuevaSolicitud({ ...nuevaSolicitud, fecha_inicio: nuevaFechaInicio, fecha_fin: '' });
                } else {
                  setNuevaSolicitud({ ...nuevaSolicitud, fecha_inicio: nuevaFechaInicio });
                }
              }}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              fullWidth
              label="Fecha Fin"
              type="date"
              value={nuevaSolicitud.fecha_fin}
              onChange={(e) => setNuevaSolicitud({ ...nuevaSolicitud, fecha_fin: e.target.value })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: nuevaSolicitud.fecha_inicio || undefined }}
              required
            />
            {nuevaSolicitud.fecha_inicio && nuevaSolicitud.fecha_fin && (() => {
              const desglose = calcularDiasDesglose(nuevaSolicitud.fecha_inicio, nuevaSolicitud.fecha_fin);
              return (
                <Box sx={{ p: 2, backgroundColor: desglose.verano > 0 ? '#ffedd5' : '#e3f2fd', borderRadius: 1 }}>
                  <Typography variant="body1">
                    <strong>Días laborables:</strong> {desglose.total}
                  </Typography>
                  {desglose.verano > 0 && desglose.normales > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 2 }}>
                      <Typography variant="body2" sx={{ color: '#0284c7', fontWeight: 500 }}>
                        {desglose.normales} día{desglose.normales !== 1 ? 's' : ''} normal{desglose.normales !== 1 ? 'es' : ''}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BeachAccessIcon sx={{ color: '#ea580c', mr: 0.5, fontSize: 18 }} />
                        <Typography variant="body2" sx={{ color: '#ea580c', fontWeight: 500 }}>
                          {desglose.verano} día{desglose.verano !== 1 ? 's' : ''} de verano
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {desglose.verano > 0 && desglose.normales === 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <BeachAccessIcon sx={{ color: '#ea580c', mr: 1 }} />
                      <Typography variant="body2" sx={{ color: '#ea580c', fontWeight: 500 }}>
                        Periodo de verano
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })()}
            <TextField
              fullWidth
              label="Comentarios (opcional)"
              multiline
              rows={2}
              value={nuevaSolicitud.comentarios}
              onChange={(e) => setNuevaSolicitud({ ...nuevaSolicitud, comentarios: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowCrearModal(false);
            setNuevaSolicitud({ persona_id: null, fecha_inicio: '', fecha_fin: '', comentarios: '' });
            setPersonaSeleccionada(null);
          }} sx={{ color: '#333' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: '#336385' }}
            onClick={handleCrearSolicitud}
          >
            Crear Solicitud
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer />
    </div>
  );
}

export default GestorAprobaciones;
